import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { v4 as uuid } from 'uuid';
import { request } from '../../../../services/request';
import { shouldReportErrorMonitor } from '../../Challenge/helpers/shouldReportErrorMonitor';
import { handleError } from '../../../ErrorHandler/errorHandlerPropsMap';
import { EDUCATION_NAMESPACE } from '../consts';
import { Role } from '@wix/ambassador-legends-persona-v1-chat/types';
import { sendChatMessage } from '@wix/ambassador-legends-persona-v1-chat/http';
import type { ChatStepContext } from '../interfaces';

export default async function (
  flowAPI: ControllerFlowAPI,
  chatId: string,
  message: string,
  store: any,
  programId: string,
  participantId: string,
  attachments?: any,
  configurationId?: string,
  stepContext?: ChatStepContext,
) {
  const userMessageId = uuid();
  const loadingMessageId = uuid();
  const now = new Date();

  // Add user message and loading assistant message
  const messagesWithLoading = [
    ...(store.messages || []),
    {
      id: userMessageId,
      chatId,
      content: message,
      role: Role.USER,
      createdDate: now,
    },
    {
      id: loadingMessageId,
      chatId,
      content: '',
      role: Role.ASSISTANT,
      createdDate: now,
      status: 'loading',
    },
  ];

  flowAPI.controllerConfig.setProps({
    messages: messagesWithLoading,
  });

  try {
    const sendChatMessageTransaction =
      flowAPI.panoramaClient.transaction('send-chat-message');

    sendChatMessageTransaction.start();

    const baseUrl = flowAPI.controllerConfig.wixCodeApi?.location?.baseUrl as
      | string
      | undefined;
    const additionalMetadata: { key: string; value: string }[] = [
      { key: 'programId', value: programId },
      { key: 'participantId', value: participantId },
    ];
    if (baseUrl) {
      additionalMetadata.push({ key: 'baseUrl', value: baseUrl });
    }
    if (stepContext?.stepId) {
      additionalMetadata.push({ key: 'stepId', value: stepContext.stepId });
      if (stepContext.stepTitle) {
        additionalMetadata.push({
          key: 'stepTitle',
          value: stepContext.stepTitle,
        });
      }
      if (stepContext.stepType) {
        additionalMetadata.push({
          key: 'stepType',
          value: stepContext.stepType,
        });
      }
    }

    const assistantResponse = (
      await request({
        flowAPI,
        options: sendChatMessage({
          chatId,
          namespaceV2: EDUCATION_NAMESPACE,
          message,
          chatConfig: {
            configurationId,
          },
          attachments,
          metadata: {
            additionalMetadata,
          },
        }),
        errorOptions: {
          errorCodesMap: { applicationError: {} },
        },
      })
    )?.data?.messages?.[0];

    sendChatMessageTransaction.finish();

    // The user switched chats while the reply was generating: the reply
    // belongs to the old chat and must not overwrite the current one
    if (store.currentChatId !== chatId) {
      return;
    }

    // Update loading message with actual response
    const messages = [
      ...(store.messages || []),
      {
        id: userMessageId,
        chatId,
        content: message,
        role: Role.USER,
        createdDate: now,
      },
      {
        ...assistantResponse,
        id: assistantResponse?.id || loadingMessageId,
        status: 'loaded',
      },
    ];

    store.messages = [...messages];

    flowAPI.controllerConfig.setProps({
      messages,
    });
  } catch (error) {
    const resolvedError = flowAPI.errorHandler.getResolvedError(error);

    handleError({
      error,
      resolvedError,
      context: 'SendChatMessage',
      preventErrorMonitorReport: !shouldReportErrorMonitor(error),
    });

    if (store.currentChatId !== chatId) {
      return;
    }

    // Remove loading message on error, keep only previous messages
    flowAPI.controllerConfig.setProps({
      messages: [...store.messages],
    });
  }
}
